class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
    this.isDesktop = window.matchMedia("(min-width: 990px)").matches;
    this.hoverTimeout = null;
    this.transparentWrapper = document.querySelector(
      ".header-wrapper--transparent",
    );

    // Pre-bind handlers so we can remove them later
    this._boundMouseEnter = this.onMouseEnter.bind(this);
    this._boundMouseLeave = this.onMouseLeave.bind(this);
    this._boundContentEnter = this.keepOpen.bind(this);
    this._boundContentLeave = this.onMouseLeave.bind(this);
    this._boundPreventClick = this.preventClick.bind(this);
    this._boundUpdateHeaderOffset = this.updateHeaderBottomPosition.bind(this);

    // Check if we're on desktop
    this.checkDevice();
    window.addEventListener("resize", this.checkDevice.bind(this));

    // Add hover events for desktop
    if (this.isDesktop) {
      this.addHoverEvents();
    }
  }

  checkDevice() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.matchMedia("(min-width: 990px)").matches;

    // If we switched between desktop and mobile, update event listeners
    if (wasDesktop !== this.isDesktop) {
      if (this.isDesktop) {
        this.addHoverEvents();
      } else {
        this.removeHoverEvents();
      }
    }
  }

  addHoverEvents() {
    // Only for mega menu type
    if (!this.mainDetailsToggle.classList.contains("mega-menu")) return;
    // Remove click events and add hover events for desktop
    this.mainDetailsToggle.addEventListener(
      "mouseenter",
      this._boundMouseEnter,
    );
    this.mainDetailsToggle.addEventListener(
      "mouseleave",
      this._boundMouseLeave,
    );
    this.content.addEventListener("mouseenter", this._boundContentEnter);
    this.content.addEventListener("mouseleave", this._boundContentLeave);

    // Prevent click from toggling on desktop
    this.mainDetailsToggle.addEventListener("click", this._boundPreventClick);

    // Also toggle aria-expanded on focus/blur for keyboard users
    const summary = this.mainDetailsToggle.querySelector("summary");
    summary &&
      summary.addEventListener("focus", () => {
        if (!this.isDesktop) return;
        summary.setAttribute("aria-expanded", true);
      });
    summary &&
      summary.addEventListener("blur", () => {
        if (!this.isDesktop) return;
        summary.setAttribute("aria-expanded", false);
      });
  }

  removeHoverEvents() {
    if (!this.mainDetailsToggle.classList.contains("mega-menu")) return;
    this.mainDetailsToggle.removeEventListener(
      "mouseenter",
      this._boundMouseEnter,
    );
    this.mainDetailsToggle.removeEventListener(
      "mouseleave",
      this._boundMouseLeave,
    );
    this.content.removeEventListener("mouseenter", this._boundContentEnter);
    this.content.removeEventListener("mouseleave", this._boundContentLeave);
    this.mainDetailsToggle.removeEventListener(
      "click",
      this._boundPreventClick,
    );
  }

  onMouseEnter() {
    if (
      !this.isDesktop ||
      !this.mainDetailsToggle.classList.contains("mega-menu")
    )
      return;

    // Clear any existing timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    // Update header position to avoid gaps/overlaps
    this.updateHeaderBottomPosition();

    // Open the menu
    this.mainDetailsToggle.setAttribute("open", "");
    const summary = this.mainDetailsToggle.querySelector("summary");
    summary && summary.setAttribute("aria-expanded", true);
    this.onToggle();

    // Add animation class
    this.content.classList.add("menu-opening");

    // If header is transparent, apply scrolled background while menu is open
    if (this.transparentWrapper) {
      this.transparentWrapper.classList.add("header-scrolled");
    }

    // Continuously keep header offset in sync while open
    window.addEventListener("scroll", this._boundUpdateHeaderOffset, {
      passive: true,
    });
    window.addEventListener("resize", this._boundUpdateHeaderOffset);
    // Double-check after next frame to account for any layout changes
    requestAnimationFrame(() => this.updateHeaderBottomPosition());
  }

  onMouseLeave() {
    if (
      !this.isDesktop ||
      !this.mainDetailsToggle.classList.contains("mega-menu")
    )
      return;

    // Set a timeout before closing to allow moving to content
    this.hoverTimeout = setTimeout(() => {
      this.close();
      this.content.classList.remove("menu-opening");
    }, 150);
  }

  keepOpen() {
    // Clear the timeout when hovering over the content
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  preventClick(event) {
    if (
      !this.isDesktop ||
      !this.mainDetailsToggle.classList.contains("mega-menu")
    )
      return;

    // Only prevent clicks on the summary element, not on links within the menu
    if (event.target.closest("summary")) {
      // Allow keyboard activation (detail === 0). Prevent mouse clicks only
      if (event.detail !== 0) {
        event.preventDefault();
      }
    }
  }

  onToggle() {
    if (!this.header) return;

    // Only update preventHide on mobile/tablet
    if (!this.isDesktop) {
      this.header.preventHide = this.mainDetailsToggle.open;
    }

    // Always refresh header position on toggle for desktop
    if (this.isDesktop) {
      this.updateHeaderBottomPosition();
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    this.content.classList.remove("menu-opening");

    // Remove listeners keeping header offset in sync
    window.removeEventListener("scroll", this._boundUpdateHeaderOffset);
    window.removeEventListener("resize", this._boundUpdateHeaderOffset);

    // Only remove header-scrolled state if no other mega-menu is currently open/hovered
    if (this.transparentWrapper) {
      const anyOpen = document.querySelector("header-menu details[open]");
      const anyHover = document.querySelector(
        "header-menu details.mega-menu:hover",
      );
      if (!anyOpen && !anyHover) {
        // Allow transparent header logic to control this on scroll
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop || 0;
        if (scrollTop <= 50) {
          this.transparentWrapper.classList.remove("header-scrolled");
        }
      }
    }
  }

  updateHeaderBottomPosition() {
    if (!this.header) return;
    const rect = this.header.getBoundingClientRect();
    // Account for optional bottom border to avoid tiny overlaps
    const wrapper = this.header.closest(".header-wrapper") || this.header;
    const hasBorder = wrapper.classList?.contains(
      "header-wrapper--border-bottom",
    );
    const borderOffset = hasBorder ? 1 : 0;
    const bottom = Math.ceil(rect.bottom - borderOffset);
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${bottom}px`,
    );
  }
}

customElements.define("header-menu", HeaderMenu);
